/**
 * CWR 1.3 — pure formatting helpers for the Home body.
 *
 * Status vocabulary is the RESOLVED D2 set (Confirmed / Awaiting payment /
 * Pending approval / Waitlisted) — it deliberately replaces the old Home drift
 * ("Pending Payment" vs "Payment Due" for the same status).
 */
import type {
  BalanceCollectionMethod,
  ResolvedLocation,
  UpcomingBookingStatus,
  WaitingOutstanding,
} from '@/types/dashboard';
import { virtualPlatformLabel } from '@telify/shared';
import { formatDollarAmount } from '@/utils/formatPrice';

type LabelledBooking = { status: UpcomingBookingStatus; outstanding?: WaitingOutstanding | null };

/**
 * manual-booking-redesign D13: a waiting manual booking that owes only forms
 * (registration counted) and no payment reads "Forms due" on the card and hero.
 */
export function isFormsDue(booking: LabelledBooking): boolean {
  const o = booking.outstanding;
  return booking.status === 'PENDING_PAYMENT' && !!o && o.forms > 0 && !o.payment;
}

/** The Home hero's label for one booking: "Forms due" for a forms-only waiting booking. */
export function getHeroStatusLabel(booking: LabelledBooking): string {
  return isFormsDue(booking) ? 'Forms due' : getBookingStatusLabel(booking.status);
}

/** The card pill for one booking: "Forms due" wears the Payment due treatment (both are due by a deadline). */
export function getBookingCardPill(booking: LabelledBooking): BookingStatusPill {
  return isFormsDue(booking) ? { label: 'Forms due', variant: 'payment' } : getBookingStatusPill(booking.status);
}

/** Payment-aware status pill label (single source of truth). */
export function getBookingStatusLabel(status: UpcomingBookingStatus): string {
  switch (status) {
    case 'CONFIRMED':
      return 'Confirmed';
    case 'PENDING_PAYMENT':
      return 'Awaiting payment';
    case 'PENDING_APPROVAL':
      return 'Pending approval';
    case 'WAITLISTED':
      return 'Waitlisted';
    default:
      return status;
  }
}

/** rd-bookings — visual variant for a status pill (drives the Frame-3 treatment). */
export type BookingStatusPillVariant = 'confirmed' | 'payment' | 'pending' | 'waitlist';

export interface BookingStatusPill {
  label: string;
  variant: BookingStatusPillVariant;
}

/**
 * rd-bookings — full status-pill descriptor (label + visual variant) for the
 * confirmed Bookings-tab / Home booking card (mockup Frame 3). Kept SEPARATE from
 * getBookingStatusLabel so the Home Next-Session hero keeps its own "Awaiting
 * payment" copy; the card uses "Payment due" with the hot+clock treatment.
 */
export function getBookingStatusPill(status: UpcomingBookingStatus): BookingStatusPill {
  switch (status) {
    case 'CONFIRMED':
      return { label: 'Confirmed', variant: 'confirmed' };
    case 'PENDING_PAYMENT':
      return { label: 'Payment due', variant: 'payment' };
    case 'PENDING_APPROVAL':
      return { label: 'Pending approval', variant: 'pending' };
    case 'WAITLISTED':
      return { label: 'Waitlisted', variant: 'waitlist' };
    default:
      return { label: status, variant: 'confirmed' };
  }
}

/**
 * rd-bookings — balance-chip copy for a DEPOSIT_PAID booking. `AT_SESSION` →
 * "$45 due at session"; `BEFORE_SESSION` → "$45 due by May 20" (from balanceDueBy;
 * falls back to "due at session" if no date). `balanceDue` is dollars (the DTO's
 * Decimal→number). Caller renders it only when paymentProgressStatus is
 * 'DEPOSIT_PAID' and balanceDue is non-null.
 */
export function formatBalanceChip(
  balanceDue: number,
  method: BalanceCollectionMethod | null,
  balanceDueBy: string | null,
): string {
  const amount = formatDollarAmount(balanceDue);
  if (method === 'BEFORE_SESSION' && balanceDueBy) {
    return `${amount} due by ${MONTH_DAY.format(new Date(balanceDueBy))}`;
  }
  return `${amount} due at session`;
}

/** Booked count for the capacity bar: capacity − spotsRemaining (clamped ≥ 0). */
export function capacityBooked(capacity: number, spotsRemaining: number): number {
  return Math.max(0, capacity - spotsRemaining);
}

/** Happening-card spots label — all non-zero counts shown; 0 → "Sold out". */
export function spotsLabel(spotsRemaining: number): string {
  if (spotsRemaining <= 0) return 'Sold out';
  return `${spotsRemaining} ${spotsRemaining === 1 ? 'spot' : 'spots'} left`;
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

const MONTH_DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const WEEKDAY = new Intl.DateTimeFormat('en-US', { weekday: 'short' });
const TIME = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

/** Hero date: "Today, May 18" / "Tomorrow, May 18" / "Mon, May 18". */
export function formatHeroDate(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const monthDay = MONTH_DAY.format(d);
  const diff = Math.round((startOfDay(d) - startOfDay(now)) / 86_400_000);
  if (diff === 0) return `Today, ${monthDay}`;
  if (diff === 1) return `Tomorrow, ${monthDay}`;
  return `${WEEKDAY.format(d)}, ${monthDay}`;
}

/** Booking-card date: "May 20, Tue". */
export function formatCardDate(iso: string): string {
  const d = new Date(iso);
  return `${MONTH_DAY.format(d)}, ${WEEKDAY.format(d)}`;
}

/** Time of day: "2:00 PM". */
export function formatCardTime(iso: string): string {
  return TIME.format(new Date(iso));
}

/** Happening-card date: "May 24 · 8:00 AM". */
export function formatEventDate(iso: string): string {
  return `${MONTH_DAY.format(new Date(iso))} · ${formatCardTime(iso)}`;
}

/** One-line location summary for the hero 1:1 row + the Upcoming card. */
export function formatLocationText(location: ResolvedLocation): string {
  // service-by-phone D14: the client is called; never the number on a card.
  if (location.isByPhone) return 'By phone';
  if (location.isVirtual) {
    // "Online on Zoom"; Other has no display name and reads "Online"
    // (virtual-sessions-finish, Client Home says where the session meets).
    const label = virtualPlatformLabel(location.virtualPlatform);
    return label ? `Online on ${label}` : 'Online';
  }
  const cityState = [location.city, location.state].filter(Boolean).join(', ');
  const parts = [location.name, cityState].filter(Boolean);
  return parts.join(' · ') || 'Location to be confirmed';
}
